/**
 * TableTabNav — sticky underline tab strip used above table-style
 * surfaces in the v2 workflow.
 *
 * Originated in Step 1's audit-form section switcher; extracted in
 * Story 4.A so Step 2-8's hierarchical view (Current TARA / Child
 * TARAs) and any future tabbed table surface can reuse the same
 * grammar (sticky to scroll viewport, edge-to-edge bottom border,
 * underline-on-active accent in `--text-primary`).
 *
 * Layout assumptions:
 *   - The caller renders this inside a scroll container with
 *     `padding: var(--space-5) var(--space-6)` (StepShell.content).
 *     The strip negates those paddings so its bottom border spans
 *     edge-to-edge across the central zone.
 *   - If you need to skip the bleed (e.g. mounted in a card with
 *     different padding), pass `bleed={false}`.
 */
import React from 'react';
import styles from './TableTabNav.module.css';

export interface TabDescriptor {
  key: string;
  label: string;
}

export interface TableTabNavProps {
  tabs: TabDescriptor[];
  activeKey: string | null | undefined;
  onChange: (key: string) => void;
  /** Optional right-aligned slot (e.g. Edit / Save / Cancel buttons). */
  right?: React.ReactNode;
  /** ARIA label for the nav landmark. */
  ariaLabel?: string;
  /** Default true — sticks to the top of the scroll viewport. */
  sticky?: boolean;
  /**
   * Default true — applies negative horizontal/top margins to bleed
   * into the StepShell content padding so the bottom border spans
   * edge-to-edge. Disable for non-StepShell parents.
   */
  bleed?: boolean;
}

export default function TableTabNav({
  tabs,
  activeKey,
  onChange,
  right,
  ariaLabel,
  sticky = true,
  bleed = true,
}: TableTabNavProps) {
  const className = [
    styles.toolbar,
    sticky ? styles.toolbarSticky : '',
    bleed ? styles.toolbarBleed : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={className}>
      <nav className={styles.tabNav} aria-label={ariaLabel}>
        {tabs.map((t) => (
          <button
            key={t.key}
            type="button"
            className={`${styles.tab} ${activeKey === t.key ? styles.tabActive : ''}`}
            onClick={() => onChange(t.key)}
            aria-current={activeKey === t.key ? 'page' : undefined}
          >
            {t.label}
          </button>
        ))}
      </nav>
      {right && <div className={styles.toolbarActions}>{right}</div>}
    </div>
  );
}
